import * as React from "react";
import {useEffect} from "react";
import {AdminAnalyticsApi} from "../../../../api/AdminAnalyticsApi";
import {LoadingSpinner} from "../../../icons/LoadingSpinner.component";
import {getCountryName} from "../../../../utils/countryUtil";
import {DEFAULT_LOGIN} from "../shared/analytics.constants";
import {useAnalyticsTabData} from "../hooks/useAnalyticsTabData";
import {LoginByMonthChart} from "./LoginByMonthChart.component";
import {LoginComparisonTable} from "./LoginComparisonTable.component";
import {CountryLoginsSection} from "./CountryLoginsSection.component";
import {NewVsReturningChart} from "./NewVsReturningChart.component";
import {LoginFrequencySection} from "./LoginFrequencySection.component";
import {numberWithCommas} from "../../../../utils/numberUtil";

interface ILoginTabProps {
    range: { startDate: string; endDate: string };
    country: string;
    onCountryOptions: (options: string[]) => void;
}

export const LoginTab: React.FC<ILoginTabProps> = (props) => {
    const {data, loading, loaded, error} = useAnalyticsTabData(
        "login",
        (startDate, endDate, c) => AdminAnalyticsApi.getLoginTab(startDate, endDate, c),
        props.range,
        props.country,
        DEFAULT_LOGIN,
    );

    // Populate the country dropdown from the all-countries login response (only when unfiltered).
    useEffect(() => {
        if (!loaded || props.country) return;
        const countries = data.countryLogins
            .filter((r) => r.total > 0 && r.country !== "Unknown" && r.country !== "XX")
            .map((r) => r.country)
            .sort((a, b) => getCountryName(a).localeCompare(getCountryName(b)));
        props.onCountryOptions(countries);
    }, [loaded, data, props.country, props.onCountryOptions]);

    if (loading) {
        return <div className="admin-analytics__tab-loading"><LoadingSpinner/></div>;
    }
    if (error) {
        return <div className="admin-analytics__error">{error}</div>;
    }

    const total = data.loginSummary.total;
    const internalPct = total > 0 ? Math.round((data.loginSummary.internal / total) * 100) : 0;
    const externalPct = total > 0 ? Math.round((data.loginSummary.external / total) * 100) : 0;

    return (
        <div>
            <section className="admin-analytics__section">
                <div className="admin-analytics__section-header">
                    <h2 className="admin-analytics__section-title">Login Trends</h2>
                    <span className="admin-analytics__section-subtitle">From the user_login table</span>
                </div>
                <p className="admin-analytics__section-desc">
                    Tracks the volume of logins over time, broken down by internal (Aon) vs. external
                    (client/third-party) users.
                    Use the month-over-month and year-over-year comparisons to spot growth or decline in
                    platform adoption.
                    Click any bar to drill into daily detail for that month. A sustained upward trend signals
                    healthy adoption;
                    flat or declining numbers may indicate engagement issues worth investigating.
                </p>
                <div className="admin-analytics__summary-grid">
                    <div className="admin-analytics__summary-card admin-analytics__summary-card--total">
                        <div className="admin-analytics__summary-label">Total Logins</div>
                        <div className="admin-analytics__summary-value">
                            {numberWithCommas(data.loginSummary.total)}
                        </div>
                        <div className="admin-analytics__summary-note">in selected period</div>
                    </div>
                    <div className="admin-analytics__summary-card admin-analytics__summary-card--internal">
                        <div className="admin-analytics__summary-label">Internal User Logins</div>
                        <div className="admin-analytics__summary-value">
                            {numberWithCommas(data.loginSummary.internal)}
                        </div>
                        <div className="admin-analytics__summary-note">{internalPct}% of all logins</div>
                    </div>
                    <div className="admin-analytics__summary-card admin-analytics__summary-card--external">
                        <div className="admin-analytics__summary-label">External User Logins</div>
                        <div className="admin-analytics__summary-value">
                            {numberWithCommas(data.loginSummary.external)}
                        </div>
                        <div className="admin-analytics__summary-note">{externalPct}% of all logins</div>
                    </div>
                </div>
                <LoginByMonthChart monthlyLogins={data.monthlyLogins} country={props.country}/>
                <LoginComparisonTable comparison={data.monthlyComparison}/>
                <CountryLoginsSection countryLogins={data.countryLogins} countryEngagement={data.countryEngagement}/>
            </section>

            {data.newVsReturning.length > 0 && (
                <NewVsReturningChart newVsReturning={data.newVsReturning}/>
            )}

            {data.loginFrequency.length > 0 && (
                <LoginFrequencySection
                    loginFrequency={data.loginFrequency}
                    avgLoginsPerUser={data.avgLoginsPerUser}
                />
            )}
        </div>
    );
};
